import { ComponentProps } from 'react';
import * as TooltipPrimitive from '@radix-ui/react-tooltip';

import { cn } from '@/lib/utils';

const TOOLTIP_BUBBLE_CLASS_NAME =
  'bg-tooltip text-tooltip-foreground border-0 px-2 py-1 text-xs rounded-md text-balance';

const TOOLTIP_ARROW_CLASS_NAME = 'fill-tooltip';

const TooltipProvider = (
  props: ComponentProps<typeof TooltipPrimitive.Provider>,
) => {
  const { delayDuration = 0, ...restProps } = props;

  return (
    <TooltipPrimitive.Provider
      data-slot="tooltip-provider"
      delayDuration={delayDuration}
      {...restProps}
    />
  );
};

const Tooltip = (props: ComponentProps<typeof TooltipPrimitive.Root>) => {
  return (
    <TooltipProvider>
      <TooltipPrimitive.Root
        data-slot="tooltip"
        {...props}
      />
    </TooltipProvider>
  );
};

const TooltipTrigger = (
  props: ComponentProps<typeof TooltipPrimitive.Trigger>,
) => {
  return (
    <TooltipPrimitive.Trigger
      data-slot="tooltip-trigger"
      {...props}
      className={cn(
        'disabled:pointer-events-auto disabled:cursor-not-allowed',
        props.className,
      )}
    />
  );
};

const TooltipContent = (
  props: ComponentProps<typeof TooltipPrimitive.Content> & {
    arrowClassName?: string;
  },
) => {
  const {
    className,
    arrowClassName,
    sideOffset = 0,
    children,
    ...restProps
  } = props;

  return (
    <TooltipPrimitive.Portal>
      <TooltipPrimitive.Content
        data-slot="tooltip-content"
        sideOffset={sideOffset}
        className={cn(
          TOOLTIP_BUBBLE_CLASS_NAME,
          'animate-in fade-in-0 zoom-in-95 data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=closed]:zoom-out-95 data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2 z-50 w-fit origin-(--radix-tooltip-content-transform-origin)',
          className,
        )}
        {...restProps}
      >
        {children}
        <TooltipPrimitive.Arrow
          className={cn(TOOLTIP_ARROW_CLASS_NAME, 'z-50', arrowClassName)}
        />
      </TooltipPrimitive.Content>
    </TooltipPrimitive.Portal>
  );
};

export {
  Tooltip,
  TOOLTIP_ARROW_CLASS_NAME,
  TOOLTIP_BUBBLE_CLASS_NAME,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
};
